import { useCallback, useEffect, useRef } from "react";

import { EventOrigin, Events, MeasureMode, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { Picker, renderActions, selectPicker } from "features/render";
import { sectionPlaneActions } from "features/sectionPlane";
import CoordinatesIcon from "media/icons/coordinates.svg?react";
import LaserIcon from "media/icons/laser.svg?react";
import PointToPointIcon from "media/icons/point-to-point.svg?react";

// import RulerIcon from "media/icons/ruler.svg?react";
import { Toolbar } from "../Toolbar";
import { flyOutMenuHandler } from "./utils";

interface MeasureFlyoutToolbarProps {
    onToolSelect?: () => void;
    id?: string;
    primaryToolbarId: string;
}

function getMeasureMode(picker: Picker): MeasureMode | undefined {
    const map: Partial<Record<Picker, MeasureMode>> = {
        [Picker.OutlineLaser]: MeasureMode.Laser,
        [Picker.Coordinates]: MeasureMode.Coordinates,
        [Picker.PointLine]: MeasureMode.PointToPoint,
        [Picker.Dimension]: MeasureMode.Dimension,
    };
    return map[picker];
}

/**
 * Flyout toolbar with measurement tool options (Laser, Coordinates, Point-to-Point, Dimension).
 */
export function MeasureFlyoutToolbar({
    onToolSelect,
    id = "measure-flyout-toolbar",
    primaryToolbarId,
}: MeasureFlyoutToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const dispatch = useAppDispatch();

    const currentPicker = useAppSelector(selectPicker);
    const onToolSelectRef = useRef(onToolSelect);
    onToolSelectRef.current = onToolSelect;

    const handlePickerClick = useCallback(
        (targetPicker: Picker) => {
            if (currentPicker === targetPicker) {
                dispatch(renderActions.stopPicker(targetPicker));
            } else {
                dispatch(renderActions.setPicker(targetPicker));
                const mode = getMeasureMode(targetPicker);
                if (mode) {
                    void track(Events.measure.enabled, { mode, origin: EventOrigin.Toolbar });
                }
            }
            onToolSelectRef.current?.();
        },
        [dispatch, currentPicker],
    );

    useEffect(() => {
        const buttons: ToolbarButtonEntry[] = [
            {
                config: {
                    id: "laser-picker",
                    icon: <LaserIcon />,
                    onClick: () => handlePickerClick(Picker.OutlineLaser),
                    title: "Laser Tool (L)",
                    tooltip: {
                        toolName: "laser",
                        shortcutKey: "L",
                        alwaysVisible: true,
                        placement: "left",
                    },
                },
                state: {
                    active: currentPicker === Picker.OutlineLaser, // Initial state
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: "coordinates-picker",
                    icon: <CoordinatesIcon />,
                    onClick: () => handlePickerClick(Picker.Coordinates),
                    title: "Coordinates Tool (C)",
                    tooltip: {
                        toolName: "coordinates",
                        shortcutKey: "C",
                        alwaysVisible: true,
                        placement: "left",
                    },
                },
                state: {
                    active: currentPicker === Picker.Coordinates, // Initial state
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: "point-to-point-picker",
                    icon: <PointToPointIcon />,
                    onClick: () => handlePickerClick(Picker.PointLine),
                    title: "Point to Point Tool (P)",
                    tooltip: {
                        toolName: "pointToPoint",
                        shortcutKey: "P",
                        alwaysVisible: true,
                        placement: "left",
                    },
                },
                state: {
                    active: currentPicker === Picker.PointLine, // Initial state
                    disabled: false,
                    visible: true,
                },
            },
            // {
            //     config: {
            //         id: "dimension-picker",
            //         icon: <RulerIcon />,
            //         onClick: () => handlePickerClick(Picker.Dimension),
            //         title: "Dimension Tool (R)",
            //         tooltip: {
            //             toolName: "dimension",
            //             shortcutKey: "R",
            //             alwaysVisible: true,
            //             placement: "left",
            //         },
            //     },
            //     state: {
            //         active: currentPicker === Picker.Dimension, // Initial state
            //         disabled: false,
            //         visible: true,
            //     },
            // },
        ];

        toolbarDispatch(
            toolbarsActions.addToolbar({
                id,
                buttons,
            }),
        );

        dispatch(sectionPlaneActions.setMenuOpen(false));

        const disposeMenuHandler = flyOutMenuHandler({
            flyoutMenuId: id,
            toolbarId: primaryToolbarId,
            close: () => {
                onToolSelectRef.current?.();
            },
        });

        return () => {
            disposeMenuHandler();
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [dispatch, toolbarDispatch, id, primaryToolbarId, handlePickerClick, currentPicker]); // Include currentPicker for initial state only

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.bulkUpdateButtonStates(id, {
                "laser-picker": { active: currentPicker === Picker.OutlineLaser },
                "coordinates-picker": { active: currentPicker === Picker.Coordinates },
                "point-to-point-picker": { active: currentPicker === Picker.PointLine },
                // "dimension-picker": { active: currentPicker === Picker.Dimension },
            }),
        );
    }, [toolbarDispatch, id, currentPicker]);

    return <Toolbar id={id} variant="fly-out" />;
}
